import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { webDtos } from '@vectronic/openApi-admin';
import { isTrapId, parseDeviceIdsSearchValue, SelectedId } from '@vectronic/util';
import { Subscription } from 'rxjs';
import { UtilService } from '../../services/util.service';
import { ColorPickerComponent } from '../color-picker/color-picker.component';

@Component({
  selector: 'vectronic-device-selector',
  templateUrl: './device-selector.component.html',
  styleUrls: ['./device-selector.component.scss'],
  imports: [
    ColorPickerComponent,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatChipsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatTooltipModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckbox,
    MatMenuModule,
  ],
})
export class DeviceSelectorComponent implements OnInit, OnDestroy {
  private utilService = inject(UtilService);
  private router = inject(Router);

  value = '';
  isFilterHidden = false;
  allDevicesSelectedCheckbox: boolean;
  private subscription: Subscription;
  selectedDeviceIds = signal<SelectedId[]>([]);
  private initialDeviceIds: SelectedId[] = [];
  sortedSelectedDeviceIds = computed(() => this.selectedDeviceIds().sort((a, b) => b.id - a.id));

  showSvgIcon = input<boolean>(false);
  isMapPage = input<boolean>(false);
  allDeviceIds = input(null, {
    transform: (value: { collars: number[]; traps: number[] }) => {
      if (!value) return null;

      const collars = value.collars ?? [];
      const traps = value.traps ?? [];
      return Array.from(new Set([...collars, ...traps]));
    },
  });
  mapPositions = input(null, {
    transform: (value: webDtos.MapPositionsList[] | null) => {
      if (!value) return;
      this.updateSelectedDeviceIdsWithColor(value);
      return value;
    },
  });

  selectedFilterIdsChangeEvent = output<SelectedId[]>();
  clearMapPositionsEvent = output();
  colorChanged = output<{ $event: string; item: SelectedId }>();
  setMapFocus = output<number>();
  getPositionsForDevices = output<SelectedId[]>();

  tooltipContent = [
    { text: '37789' },
    { text: 'Collar 37789' },
    { text: '37789-37792' },
    { text: '37789-37792,37796,37785' },
    { text: '37789,37790,37791,37792' },
    { text: '37789 37790 37791 37792' },
    { text: 'Collar 37789 Collar 37790 Collar 37791 Collar 37792' },
    { text: 'Collar 37789,Collar 37790,Collar 37791,Collar 37792' },
  ];

  constructor() {
    effect(() => {
      const normalizeDevices = (devices: SelectedId[]) =>
        devices.map(({ id, checked }) => ({ id, checked })).sort((a, b) => a.id - b.id);

      const initial = JSON.stringify(normalizeDevices(this.initialDeviceIds));
      const current = JSON.stringify(normalizeDevices(this.selectedDeviceIds()));

      const hasDeviceIdsChanged = initial !== current;
      if (hasDeviceIdsChanged) {
        this.utilService.clearLocalDeviceState();
        this.utilService.clearDeviceListFilters();
      }
    });
  }

  ngOnInit() {
    const urlTree = this.router.parseUrl(this.router.url);
    const deviceIdsFilter: string = urlTree.queryParams['deviceIdsFilter'];
    if (deviceIdsFilter) {
      const deviceIdsFilterIds = parseDeviceIdsSearchValue(deviceIdsFilter);
      this.utilService.setSessionDeviceIds(
        deviceIdsFilterIds.map((id) => ({ id: id, checked: true })),
      );
    }

    if (this.utilService.getSessionDeviceIds().length) {
      const sessionDeviceIds = this.utilService.getSessionDeviceIds();
      this.selectedDeviceIds.set(sessionDeviceIds);
      this.initialDeviceIds = [...sessionDeviceIds];
      this.setDataAndEmitPositionsEvent();
    }
  }

  selectedCollars(value: string): void {
    const rawTerms = value
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t !== '');

    const allIdsArray = (this.allDeviceIds() ?? []) as number[];
    const allIdSet = new Set(allIdsArray);

    const validIds: number[] = [];
    const unknownTerms: string[] = [];

    for (const term of rawTerms) {
      const termIds = parseDeviceIdsSearchValue(term);
      if (!termIds.length) {
        unknownTerms.push(term);
        continue;
      }

      const existingIdsInTerm = termIds.filter((id) => allIdSet.has(id));
      if (!existingIdsInTerm.length) {
        unknownTerms.push(term);
        continue;
      }

      validIds.push(...existingIdsInTerm);
    }

    // Add new valid ids (dedup + skip already selected) in one update
    this.selectedDeviceIds.update((ids) => {
      const currentIds = new Set(ids.map((i) => i.id));
      const toAdd = Array.from(new Set(validIds)).filter((id) => !currentIds.has(id));
      const next = toAdd.length
        ? [...ids, ...toAdd.map((id) => ({ id, checked: true as const }))]
        : ids;

      // Sort: checked first; preserve relative order otherwise
      return next.slice().sort((a, b) => (b.checked ? 1 : -1) - (a.checked ? 1 : -1));
    });

    // Keep only the unknown/nonexistent terms in the input
    this.value = Array.from(new Set(unknownTerms)).join(', ');
    this.setDataAndEmitPositionsEvent();
  }

  removeDeviceId(selectedNumber: number) {
    this.selectedDeviceIds.update((selectedIds) =>
      selectedIds.filter(({ id }) => id !== selectedNumber),
    );
    this.setDataAndEmitPositionsEvent();
  }

  navigateToDeviceDetails(id: number) {
    const basePath = isTrapId(id) ? '/devices/traps' : '/devices/collars';

    this.router.navigate([basePath], { queryParams: { id } });
  }

  private setDataAndEmitPositionsEvent() {
    this.selectedFilterIdsChangeEvent.emit(this.selectedDeviceIds());
    this.utilService.setSessionDeviceIds(this.selectedDeviceIds());
    if (this.selectedDeviceIds().length === 0) {
      this.utilService.clearLocalDeviceState();
      this.utilService.clearDeviceListFilters();
    }
    this.allDevicesSelectedCheckbox = this.selectedDeviceIds().every(({ checked }) => checked);

    if (this.isMapPage()) {
      this.getPositionsForDevices.emit(this.selectedDeviceIds());
    }
    this.isFilterHidden = this.selectedDeviceIds().length <= 0;
  }

  clearSelection() {
    this.selectedFilterIdsChangeEvent.emit([]);
    this.selectedDeviceIds.set([]);
    this.utilService.setSessionDeviceIds([]);
    this.clearMapPositionsEvent.emit();

    this.utilService.clearLocalDeviceState();
    this.utilService.clearDeviceListFilters();
  }

  selectAll({ checked }: MatCheckboxChange) {
    this.selectedDeviceIds.update((selectedIds) =>
      selectedIds.map((selectedId) => ({ ...selectedId, checked })),
    );
    this.selectedFilterIdsChangeEvent.emit(checked ? this.selectedDeviceIds() : []);
    this.utilService.setSessionDeviceIds(this.selectedDeviceIds());
    this.allDevicesSelectedCheckbox = this.selectedDeviceIds().every(({ checked }) => checked);

    if (this.isMapPage()) {
      this.getPositionsForDevices.emit(this.selectedDeviceIds());
    }
  }

  onCheckboxChange(id: number, { checked }: MatCheckboxChange) {
    this.selectedDeviceIds.update((selectedIds) =>
      selectedIds.map((selectedId) =>
        selectedId.id === id ? { ...selectedId, checked } : selectedId,
      ),
    );
    this.setDataAndEmitPositionsEvent();
  }

  tryTooltip(input: string) {
    this.value = input;
    this.selectedCollars(input);
  }

  isValidId(id: number) {
    return isTrapId(id);
  }

  toggleFilter() {
    this.isFilterHidden = !this.isFilterHidden;
    if (this.isFilterHidden) {
      this.clearSelectedItems();
    }
  }

  clearSelectedItems() {
    this.selectedDeviceIds.set([]);
    this.selectedFilterIdsChangeEvent.emit([]);
    this.utilService.setSessionDeviceIds([]);
    this.utilService.clearLocalDeviceState();
    this.utilService.clearDeviceListFilters();
    this.value = '';

    if (this.isMapPage()) {
      this.getPositionsForDevices.emit(this.selectedDeviceIds());
    }
  }

  private updateSelectedDeviceIdsWithColor(mapPositions: webDtos.MapPositionsList[]) {
    const colorMap = new Map<number, string>(mapPositions.map(({ id, color }) => [id, color]));

    const updatedSelectedDevices = this.selectedDeviceIds().map((selectedId) =>
      colorMap.has(selectedId.id)
        ? { ...selectedId, color: colorMap.get(selectedId.id) }
        : selectedId,
    );
    this.selectedDeviceIds.set(updatedSelectedDevices);
  }

  ngOnDestroy() {
    if (this.subscription) this.subscription.unsubscribe();
  }
}
